/**
 * Home Page Wrapper for ISR
 *
 * This component uses STATIC IMPORTS for optimal ISR performance.
 * - No dynamic() imports - all components are bundled together
 * - No LazyLoadBlock - full content is pre-rendered in HTML
 * - Components are imported directly for faster hydration
 *
 * The old pages (home.desktop.page.tsx, home.mobile.page.tsx) are DEPRECATED.
 */

import React, {useMemo} from 'react';
import {useModalAction} from '@/_application/core/components/ui/modal/modal.context';
import {authorizationAtom} from '@/store/authorization-atom';
import {useAtom} from 'jotai';
import 'swiper/swiper-bundle.css';
import {useEntrancePopupController} from '@/_application/pages/home/hooks/use-entrance-popup-controller';
import {DynamicPageServerSideProps} from '@/_application/core/types';
import {useIsrPageInit} from '@/_application/pages/_isr';
import {Customer} from '@/__generated__/__types__';

// ============================================
// STATIC IMPORTS - Desktop Components
// ============================================
import Sidebar from '@/_application/themes/kidsplaza-spring/layouts/components/sidebar/kidsplaza-spring.desktop.sidebar';
import MainBannerSwiper from '@/_application/pages/home/components/main-banner-swiper';
import ShortcutSwiper from '@/_application/pages/home/components/shortcut-swiper';
import SubBannerSwiper from '@/_application/pages/home/components/sub-banner-swiper';
import {FlashSalesSlider} from '@/_application/modules/flash-sales/components/home-page/flash-sales-slider';
import HighlightCategories from '@/_application/pages/home/components/highlight-categories/highlight-categories.desktop';
import PopularBrand from '@/_application/pages/home/components/popular-brand.desktop';
import HomeBannerSwiper from '@/_application/pages/home/components/home-banner-swiper';
import HighLightTabs from '@/_application/modules/products/components/highlight-products-block/highlight-tabs';
import ProductCategorySpotlightList from '@/_application/pages/home/components/product-category-spotlight/product-category-spotlight-list.desktop';
import ProductsSuggestion from '@/_application/modules/products/components/products-suggestion/products-suggestion';
import UsefulInformation from '@/_application/modules/useful-information/useful-info.desktop';
import PopularKeywordsHome from '@/_application/modules/home/popular-keyword-home';

// ============================================
// STATIC IMPORTS - Mobile Components
// ============================================
import SubBanner from '@/_application/pages/home/components/sub-banner.mobile';
import HomeBannerSwiperMobile from '@/_application/pages/home/components/home-banner-swiper.mobile';
import HomeCatalog from '@/_application/pages/home/components/home-catalog';
import PopularCatalog from '@/_application/pages/home/components/popular-catalog.mobile';
import HighlightProductBlockMobile from '@/_application/modules/products/components/highlight-products-block/highlight-product-block.mobile';
import RecommendedProductBlock from '@/_application/pages/home/components/recommend-product-block.mobile';
import {SmallBrandSwiperHome} from '@/_application/pages/home/components/small-brand-swiper-home.mobile';
import UsefulInformationMobile from '@/_application/modules/useful-information/useful-info.mobile';
import ProductCategorySpotlightListMobile from '@/_application/pages/home/components/product-category-spotlight/product-category-spotlight-list.mobile';
import MidHomeBannerSwiperMobile from '@/_application/pages/home/components/mid-home-banner-swiper.mobile';
import {FlashSalesSliderMobile} from '@/_application/modules/flash-sales/components/home-page/flash-sales-slider.mobile';
import {GuestInfoCard, UserInfoCard, UserInfoCardMobile} from '@/_application/modules/auth/components';
import {CouponWidget} from '@/nova/modules/coupon-wallet';
import {CouponWidgetContent} from '@/nova/modules/sales-rule';
import {useSession} from 'next-auth/react';

export interface HomePageWrapperProps {
    variables?: DynamicPageServerSideProps['variables'];
    isMobile?: boolean;
    layout?: DynamicPageServerSideProps['layout'];
}

/**
 * Desktop Home Page for ISR
 * All components are STATICALLY imported - no dynamic(), no LazyLoadBlock
 * Hashtags are pre-fetched on server for SEO
 */
function HomePageDesktopIsr({
    variables,
    customer,
}: Readonly<{
    variables: DynamicPageServerSideProps['variables'];
    customer?: Customer | null;
}>) {
    const {homeData, categories} = variables;
    const {
        mainBanner,
        subBanner,
        homepageHashtags,
        highlightCategories,
        highlightBrands,
        homepageFeaturedBlocks,
        midHomePageBanners,
        flashSaleData,
        interestedProducts,
        highlightTabsData,
    } = homeData ?? {
        mainBanner: [],
        subBanner: [],
        categories: [],
        highlightCategories: [],
        highlightBrands: null,
        homepageFeaturedBlocks: [],
        midHomePageBanners: [],
        flashSaleData: undefined,
        interestedProducts: [],
        highlightTabsData: undefined,
    };
    const {status} = useSession();
    const {openModal} = useModalAction();
    const isAuthorize = useMemo(() => status === 'authenticated', [status]);

    useEntrancePopupController();

    return (
        <main className="flex flex-col items-center justify-center bg-secondary overflow-x-auto overflow-y-hidden [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar:horizontal]:hidden">
            <div className="flex min-h-106.5 flex-wrap justify-center gap-4 py-5">
                <Sidebar categories={categories} isHeader={false} />

                <div className="flex w-174.5 flex-col justify-between gap-5">
                    <MainBannerSwiper mainBanner={mainBanner} />
                    <ShortcutSwiper isAuthorize={isAuthorize} onOpenLoginModal={() => openModal('LOGIN_VIEW')} />
                </div>

                {isAuthorize ? <UserInfoCard /> : <GuestInfoCard />}
            </div>

            <div className="w-174.5 lg:w-237.5 xl:w-299">
                <SubBannerSwiper subBanner={subBanner} />
            </div>

            <div className="w-screen">
                <FlashSalesSlider flashSaleData={flashSaleData} />
            </div>

            <div className="mt-5 w-[698px] lg:w-[950px] xl:w-[1204px]">
                <CouponWidgetContent variant="voucher" />
            </div>

            <div className="mt-5 w-[698px] lg:w-[950px] xl:w-[1204px]">
                <CouponWidgetContent variant="eWalletVoucher" />
            </div>

            <div className="my-5 min-h-68 w-174.5 lg:w-237.5 xl:w-301">
                <HighlightCategories highlightCategories={highlightCategories} />
            </div>

            <div className="min-h-49.75">
                <div className="mb-5 w-174.5 lg:w-237.5 xl:w-301">
                    <PopularBrand highlightBrands={highlightBrands} />
                </div>
            </div>

            <div className="min-h-62">
                <div className="mb-5 w-174.5 lg:w-237.5 xl:w-299">
                    <HomeBannerSwiper midHomePageBanners={midHomePageBanners} />
                </div>
            </div>

            <div className="min-h-[56.188rem] w-full bg-[#dff3fe]">
                <div className="mx-auto w-174.5 pb-5 pt-5 lg:w-237.5 xl:w-299">
                    <HighLightTabs highlightTabsData={highlightTabsData} />
                </div>
            </div>

            <div className="min-h-880 w-full">
                <ProductCategorySpotlightList
                    homepageFeaturedBlocks={homepageFeaturedBlocks}
                    highlightBrands={highlightBrands}
                />
            </div>

            <div className="min-h-[54.313rem] w-full bg-[#20C4F433]">
                <div className="mx-auto w-174.5 pb-5 pt-5 lg:w-237.5 xl:w-299">
                    <ProductsSuggestion interestedProducts={interestedProducts} />
                </div>
            </div>

            <div className="min-h-67">
                <UsefulInformation className="w-174.5 pt-5 lg:w-237.5 xl:w-299" />
            </div>

            <PopularKeywordsHome serverHashtags={homepageHashtags} />
        </main>
    );
}

/**
 * Mobile Home Page for ISR
 * All components are STATICALLY imported - no dynamic(), no LazyLoadBlock
 * Hashtags are pre-fetched on server for SEO
 */
function HomePageMobileIsr({
    variables,
    customer,
}: Readonly<{
    variables: DynamicPageServerSideProps['variables'];
    customer?: Customer | null;
}>) {
    const {homeData} = variables;
    const {mainBanner, subBanner, homepageHashtags, homepageFeaturedBlocks, highlightBrands, flashSaleData} =
        homeData ?? {
            mainBanner: [],
            subBanner: [],
            homepageHashtags: [],
            homepageFeaturedBlocks: [],
            highlightBrands: null,
            flashSaleData: undefined,
        };

    useEntrancePopupController();

    return (
        <div className="w-full">
            <div className="relative h-80.5 w-screen">
                <HomeBannerSwiperMobile banners={mainBanner} />
                <div className="absolute -bottom-6 z-3 w-full">
                    <UserInfoCardMobile />
                </div>
            </div>
            <HomeCatalog />
            <SubBanner banners={subBanner} />
            <FlashSalesSliderMobile flashSaleData={flashSaleData} />

            <div className="mt-4">
                <CouponWidgetContent variant="voucher" isMobile={true} />
            </div>
            <div className="mt-4">
                <CouponWidgetContent variant="eWalletVoucher" isMobile={true} />
            </div>

            <PopularCatalog />
            <div className="bg-white">
                <div className="ml-4 pt-4">
                    <SmallBrandSwiperHome />
                </div>
                <div className="py-4">
                    <MidHomeBannerSwiperMobile />
                </div>
            </div>
            <HighlightProductBlockMobile />
            <ProductCategorySpotlightListMobile
                homepageFeaturedBlocks={homepageFeaturedBlocks}
                highlightBrands={highlightBrands}
            />
            <RecommendedProductBlock />
            <UsefulInformationMobile />
            <PopularKeywordsHome serverHashtags={homepageHashtags} />
        </div>
    );
}

/**
 * Home Page Wrapper for ISR
 * Entry point that selects mobile or desktop version
 * Uses centralized useIsrPageInit to handle storeServerData and get customer
 */
export const HomePageWrapper: React.FC<HomePageWrapperProps> = ({variables, isMobile}) => {
    // Initialize ISR page - handles storeServerData and returns customer
    const customer = useIsrPageInit(variables);

    if (!variables) {
        return null;
    }

    if (isMobile) {
        return <HomePageMobileIsr variables={variables} customer={customer} />;
    }

    return <HomePageDesktopIsr variables={variables} customer={customer} />;
};

export default HomePageWrapper;
